"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SendCounterpartyAgreementSchema = exports.COUNTERPARTY_AGREEMENTS = exports.COUNTERPARTY_SUBJECTS = void 0;
const zod_1 = require("zod");
/**
 * THE FOUR COUNTERPARTY AGREEMENTS, as one table.
 *
 * A vendor agreement, an employment agreement, a partner agreement and a
 * software agreement differ in exactly four ways: who signs it, what process
 * opened it, which merge-field group describes the counterparty, and which
 * publish context its documents must declare. Everything else — the request,
 * the pack, freezing a version, emailing an OTP link, collecting the
 * signature — is the SAME engine that already sends hardware agreements.
 *
 * Written down once, here, because three copies of "a vendor signs as `vendor`
 * in a `vendor` context" (service, UI, and tests) is three places for it to
 * drift. The service builds merge data from this; the UI builds its form from
 * it; both read the same row.
 */
exports.COUNTERPARTY_SUBJECTS = ['vendor', 'employee', 'partner', 'software'];
exports.COUNTERPARTY_AGREEMENTS = {
    vendor: {
        signerRole: 'vendor',
        contextType: 'vendor_agreement',
        mergeContext: 'vendor',
        fieldGroup: 'vendor',
        label: 'Vendor / supplier',
        hint: 'A company we buy from or contract with. We are the other party.',
    },
    employee: {
        signerRole: 'employee',
        contextType: 'employment',
        mergeContext: 'employment',
        fieldGroup: 'employee',
        label: 'Employee',
        hint: 'The EMPLOYER is the tenant, not us — the tenant\'s own details fill the company side.',
    },
    partner: {
        signerRole: 'partner',
        contextType: 'partner_agreement',
        mergeContext: 'partner',
        fieldGroup: 'partner',
        label: 'Partner / ISO',
        hint: 'An ISO or affiliate we pay residuals to, including their commission plan and payout terms.',
    },
    software: {
        // The tenant's own admin signs for software — there is no outside party.
        signerRole: 'tenant_admin',
        contextType: 'software_agreement',
        mergeContext: 'deal_software',
        fieldGroup: 'package',
        label: 'Software / packages',
        hint: 'What a tenant is licensing. Package rows also fill a package schedule.',
    },
};
/**
 * The request body both the admin and internal routes accept.
 *
 * `fields` carries the counterparty's own details in the PROVIDER's input
 * shape (camelCase — `legalName`, not `vendor_legal_name`). The prefixing is
 * `composeMergeData`'s job, and doing it here as well would be the second copy
 * of the naming rule the catalog exists to prevent.
 */
exports.SendCounterpartyAgreementSchema = zod_1.z.object({
    subject: zod_1.z.enum(exports.COUNTERPARTY_SUBJECTS),
    /** The vendor / staff member / partner / package this agreement is ABOUT. */
    subjectId: zod_1.z.string().min(1).max(128),
    /**
     * Which tenant the request belongs to. `platform` for a vendor or partner
     * agreement, which is ours rather than any tenant's.
     */
    tenantId: zod_1.z.string().min(1),
    /**
     * WHO SIGNS FOR US on this agreement. Absent uses the company profile's
     * default signatory (normally the CEO); naming one lets a sales rep own the
     * deal and countersign it themselves.
     */
    companySignatoryId: zod_1.z.string().min(1).optional(),
    /** Named pack, or an explicit document list — one or the other. */
    packId: zod_1.z.string().min(1).optional(),
    documentIds: zod_1.z.array(zod_1.z.string().min(1)).min(1).max(20).optional(),
    /** The human who signs opposite us. */
    party: zod_1.z.object({
        name: zod_1.z.string().trim().min(1),
        email: zod_1.z.string().email(),
    }),
    /** Provider-shaped counterparty details. Values are strings; blanks are dropped. */
    fields: zod_1.z.record(zod_1.z.string()).default({}),
    /**
     * SOFTWARE ONLY — the packages being licensed. Fills `package_*` and the
     * package schedule. Ignored for the other three subjects rather than
     * silently accepted, so a caller cannot half-configure a vendor agreement.
     */
    packages: zod_1.z.array(zod_1.z.object({
        name: zod_1.z.string().trim().min(1),
        monthlyPriceCents: zod_1.z.number().int().min(0).optional(),
        features: zod_1.z.array(zod_1.z.string()).optional(),
        limits: zod_1.z.string().optional(),
        quantity: zod_1.z.number().int().positive().optional(),
    })).optional(),
}).refine((b) => !!b.packId !== !!(b.documentIds && b.documentIds.length), {
    message: 'name a pack OR list documents, not both and not neither — a request with no '
        + 'documents has nothing to send, and two sources would silently disagree about which won',
    path: ['packId'],
}).refine((b) => b.subject !== 'software' || (b.packages?.length ?? 0) > 0, {
    message: 'a software agreement must name at least one package — the document describes what '
        + 'is being licensed, and an empty licence grants nothing',
    path: ['packages'],
});
//# sourceMappingURL=counterpartyAgreements.js.map